/**
 * Authoritative active reading time accumulator for the reader-time study.
 *
 * Accumulates elapsed milliseconds only while the timer is running AND the
 * user is not idle. The idle signal comes from the same `useIdleTimer` that
 * drives the visual pill, so the two sources of truth are in sync.
 *
 * Usage: mount once per case-viewer, call `pause()` / `resume()` from the
 * idle callbacks, call `readActiveMs()` at sign time to get the integer
 * ms value that goes on the `/api/study/sign` payload.
 *
 * Design invariants:
 * - `pause()` is idempotent (double-pause doesn't accumulate phantom gaps).
 * - `resume()` is idempotent (double-resume doesn't lose time).
 * - `reset()` clears all state so the hook can be reused across cases without
 *   remounting (keyed on studyUid in LabelingPage).
 * - All timestamps are `performance.now()` (monotonic) — immune to system-clock
 *   drift mid-case.
 */
import { useCallback, useMemo, useRef } from "react";

export interface ActiveReadingTimerHandle {
  /** Start accumulating from zero (call when the rad clicks Start). */
  start: () => void;
  /** Pause accumulation (call on idle transition). */
  pause: () => void;
  /** Resume accumulation (call on active transition). */
  resume: () => void;
  /** Reset to initial state (call on case change before `start`). */
  reset: () => void;
  /**
   * Returns the total idle-subtracted active reading time in milliseconds,
   * rounded to the nearest integer. Safe to call while running — returns the
   * time accrued up to this instant.
   */
  readActiveMs: () => number;
}

export function useActiveReadingTimer(): ActiveReadingTimerHandle {
  // Accumulated ms from fully-closed intervals (paused or stopped segments).
  const accumulatedMsRef = useRef(0);
  // performance.now() when the current active segment started. null = not running.
  const segmentStartRef = useRef<number | null>(null);

  const start = useCallback(() => {
    // Guard: already running (defensive; LabelingPage only calls once per case).
    if (segmentStartRef.current !== null) return;
    segmentStartRef.current = performance.now();
  }, []);

  const pause = useCallback(() => {
    if (segmentStartRef.current === null) return; // already paused / not started
    const now = performance.now();
    accumulatedMsRef.current += now - segmentStartRef.current;
    segmentStartRef.current = null;
  }, []);

  const resume = useCallback(() => {
    if (segmentStartRef.current !== null) return; // already running
    segmentStartRef.current = performance.now();
  }, []);

  const reset = useCallback(() => {
    accumulatedMsRef.current = 0;
    segmentStartRef.current = null;
  }, []);

  const readActiveMs = useCallback((): number => {
    const inFlight =
      segmentStartRef.current !== null ? performance.now() - segmentStartRef.current : 0;
    return Math.round(accumulatedMsRef.current + inFlight);
  }, []);

  // Memoize the returned handle so consumers' effect/callback dep arrays keyed
  // on it don't re-fire every render (the methods are already stable
  // useCallbacks). Mirrors useStudyEventLog. Without this, LabelingPage's
  // case-reset effect (deps include this handle) re-runs every render and
  // resets studyHasStarted — the "Start case" lock screen then never dismisses.
  return useMemo(
    () => ({ start, pause, resume, reset, readActiveMs }),
    [start, pause, resume, reset, readActiveMs],
  );
}
